import React, { useState } from 'react';
import { X, PaperPlaneTilt, Check, EnvelopeSimple, Phone, Buildings } from '@phosphor-icons/react';
import { motion, AnimatePresence } from '@/lib/motion';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { validateName, validateEmail, validatePhone, parseApiError } from '../utils/formValidation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InquiryModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ 
    name: '', 
    company: '', 
    email: '', 
    phone: '', 
    projectType: '',
    budget: '',
    timeline: '',
    message: '' 
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError =
      validateName(formData.name) ||
      validateEmail(formData.email) ||
      validatePhone(formData.phone);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        onClose();
        setFormData({ name: '', company: '', email: '', phone: '', projectType: '', budget: '', timeline: '', message: '' });
      }, 2000);
    } catch (err) {
      setError(parseApiError(err.response?.data?.detail, 'Could not send your inquiry. Please try again.'));
    }
    setLoading(false);
  };

  const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm" />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative bg-white w-full max-w-[600px] max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-obsidian text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-display text-sm font-bold uppercase tracking-[0.15em]">
                  START YOUR PROJECT
                </h2>
                <p className="font-body text-[11px] text-white/60 uppercase tracking-wider mt-0.5">
                  TELL US ABOUT YOUR VISION
                </p>
              </div>
              <button type="button"
                onClick={onClose}
                className="p-2 hover:bg-white/10 transition-colors"
                data-testid="close-inquiry-modal"
              >
                <X weight="bold" className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="font-body text-[10px] font-semibold text-obsidian uppercase tracking-[0.15em] block mb-1.5">NAME *</label>
                  <Input 
                    value={formData.name} onChange={set('name')} placeholder="Your name" required
                    className="h-9 text-[13px] border-black/10 rounded-none focus:border-obsidian focus:ring-0"
                  />
                </div>
                <div>
                  <label className="font-body text-[10px] font-semibold text-obsidian uppercase tracking-[0.15em] block mb-1.5">COMPANY</label>
                  <Input 
                    value={formData.company} onChange={set('company')} placeholder="Studio / Firm"
                    className="h-9 text-[13px] border-black/10 rounded-none focus:border-obsidian focus:ring-0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="font-body text-[10px] font-semibold text-obsidian uppercase tracking-[0.15em] block mb-1.5">EMAIL *</label>
                  <Input 
                    type="email" value={formData.email} onChange={set('email')} placeholder="you@email.com" required
                    className="h-9 text-[13px] border-black/10 rounded-none focus:border-obsidian focus:ring-0"
                  />
                </div>
                <div>
                  <label className="font-body text-[10px] font-semibold text-obsidian uppercase tracking-[0.15em] block mb-1.5">PHONE</label>
                  <Input 
                    type="tel" value={formData.phone} onChange={set('phone')} placeholder="+971 XX XXX XXXX"
                    className="h-9 text-[13px] border-black/10 rounded-none focus:border-obsidian focus:ring-0"
                  />
                </div>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="font-body text-[10px] font-semibold text-obsidian uppercase tracking-[0.15em] block mb-1.5">PROJECT TYPE</label>
                  <select 
                    value={formData.projectType} onChange={set('projectType')}
                    className="w-full h-9 text-[13px] border border-black/10 px-3 focus:border-obsidian focus:ring-0 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    <option value="facade">FACADE</option>
                    <option value="interior">INTERIOR</option>
                    <option value="signage">SIGNAGE</option>
                    <option value="sculpture">SCULPTURE</option>
                    <option value="retail">RETAIL</option>
                    <option value="other">OTHER</option>
                  </select>
                </div>
                <div>
                  <label className="font-body text-[10px] font-semibold text-obsidian uppercase tracking-[0.15em] block mb-1.5">BUDGET RANGE</label>
                  <select 
                    value={formData.budget} onChange={set('budget')}
                    className="w-full h-9 text-[13px] border border-black/10 px-3 focus:border-obsidian focus:ring-0 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    <option value="under-100k">UNDER 100K</option>
                    <option value="100k-500k">100K - 500K</option>
                    <option value="500k-1m">500K - 1M</option>
                    <option value="over-1m">OVER 1M</option>
                  </select>
                </div>
                <div>
                  <label className="font-body text-[10px] font-semibold text-obsidian uppercase tracking-[0.15em] block mb-1.5">TIMELINE</label>
                  <select 
                    value={formData.timeline} onChange={set('timeline')}
                    className="w-full h-9 text-[13px] border border-black/10 px-3 focus:border-obsidian focus:ring-0 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    <option value="urgent">URGENT</option>
                    <option value="1-3months">1-3 MONTHS</option>
                    <option value="3-6months">3-6 MONTHS</option>
                    <option value="6months+">6+ MONTHS</option>
                  </select>
                </div>
              </div>

              <div className="mb-5">
                <label className="font-body text-[10px] font-semibold text-obsidian uppercase tracking-[0.15em] block mb-1.5">PROJECT BRIEF *</label>
                <Textarea 
                  value={formData.message} onChange={set('message')} 
                  placeholder="Describe your project requirements, materials, design intent..." rows={4} required
                  className="text-[13px] border-black/10 rounded-none resize-none focus:border-obsidian focus:ring-0"
                />
              </div>

              {error && (
                <p className="mb-3 p-3 bg-red-50 border border-red-100 text-red-700 text-[12px]" data-testid="inquiry-error">
                  {error}
                </p>
              )}

              <motion.button
                type="submit"
                disabled={submitted || loading}
                className={`w-full py-3 font-body text-[11px] font-bold uppercase tracking-[0.15em] transition-colors flex items-center justify-center gap-2 ${
                  submitted ? 'bg-green-600 text-white' : 'bg-obsidian text-white hover:bg-bronze hover:text-obsidian'
                }`}
                whileHover={{ scale: submitted ? 1 : 1.01 }}
              >
                {submitted ? (
                  <><Check weight="bold" className="w-4 h-4" />MESSAGE SENT</>
                ) : loading ? (
                  'SENDING...'
                ) : (
                  <><PaperPlaneTilt weight="bold" className="w-4 h-4" />SUBMIT INQUIRY</>
                )}
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InquiryModal;
